import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiCheck } from "react-icons/fi";
import {
  getCustomerSession,
  mergeCustomerSession,
} from "../../utils/applyFlowSession";
import { persistJourneyFromApi } from "../../utils/applyNewJourney";
import { getApiMessage } from "../../config/taskApi";
import fundwalelogo from "../../assets/images/Fundswalle_Logo.png";

const BANK_ANALYZER_URL = "https://fundswalla.in/Api/TaskApi/bankBre";

const AACallbackPage = ({ setScreen }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");
  const [callbackData, setCallbackData] = useState(null);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    const handleAACallback = async () => {
      try {
        console.log("=================================");
        console.log("ACCOUNT AGGREGATOR CALLBACK PAGE");
        console.log("=================================");

        const params = new URLSearchParams(window.location.search);

        const leadId =
          params.get("lead_id") || params.get("leadId") || "";

        const transId =
          params.get("transID") ||
          params.get("trans_id") ||
          params.get("txn_id") ||
          "";

        const reqId =
          params.get("reqID") ||
          params.get("request_id") ||
          params.get("req_id") ||
          "";

        const callbackStatus = params.get("status") || "";

        const error =
          params.get("error") || params.get("message") || "";

        const data = {
          leadId,
          transId,
          reqId,
          callbackStatus,
          error,
        };

        console.log("AA CALLBACK URL:", window.location.href);
        console.log("AA CALLBACK DATA:", data);

        setCallbackData(data);

        if (error) {
          setStatus("failed");
          setMessage(error);
          setLoading(false);
          return;
        }

        // Success UI always — params kept in state for Continue / Bank BRE only
        setStatus("received");
        setMessage("");
        setLoading(false);

        if (window.opener && !window.opener.closed) {
          console.log("=================================");
          console.log("SENDING AA COMPLETION TO TAB 1");
          console.log("=================================");

          window.opener.postMessage(
            {
              type: "AA_COMPLETED",
              leadId,
              transId,
              reqId,
              status: callbackStatus || "success",
            },
            window.location.origin
          );

          setTimeout(() => {
            window.close();
          }, 500);
          return;
        }

        console.log("=================================");
        console.log("AA CALLBACK (SAME TAB) — SUCCESS UI");
        console.log("=================================");

        const storedLead = String(
          leadId || getCustomerSession()?.lead_id || ""
        ).trim();

        mergeCustomerSession({
          ...(storedLead ? { lead_id: storedLead } : {}),
          last_screen: "aaCallback",
          saved_fields: {
            ...(getCustomerSession()?.saved_fields || {}),
            last_screen: "aaCallback",
          },
        });
      } catch (err) {
        console.error("AA CALLBACK ERROR:", err);
        setStatus("failed");
        setMessage(
          err?.message || "Unable to process Account Aggregator callback."
        );
        setLoading(false);
      }
    };

    handleAACallback();
  }, []);

  const handleContinue = async () => {
    if (continuing) return;

    try {
      setContinuing(true);
      setLoading(true);
      setMessage("");

      console.log("=================================");
      console.log("BANK ANALYZER START");
      console.log("=================================");

      const stored = getCustomerSession() || {};

      const leadId = String(
        callbackData?.leadId || stored?.lead_id || ""
      ).trim();

      if (!leadId) {
        throw new Error("Lead ID is missing");
      }

      const declaredIncome = Number(
        String(
          stored?.monthly_income ||
            stored?.saved_fields?.monthly_income ||
            stored?.saved_fields?.monthlyIncome ||
            0
        ).replace(/,/g, "")
      );

      const loanAmount = Number(
        String(
          stored?.loan_amount ||
            stored?.saved_fields?.loan_amount ||
            stored?.saved_fields?.loanAmount ||
            0
        ).replace(/,/g, "")
      );

      const payload = {
        lead_id: leadId,
        declared_income: declaredIncome,
        loan_amount: loanAmount,
      };

      console.log("========== BANK ANALYZER PAYLOAD ==========");
      console.log(JSON.stringify(payload, null, 2));

      const response = await axios.post(BANK_ANALYZER_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
        validateStatus: () => true,
      });

      console.log("========== BANK ANALYZER RESPONSE ==========");
      console.log("HTTP STATUS:", response.status);
      console.log("RESPONSE:", response.data);

      if (
        response.status >= 200 &&
        response.status < 300 &&
        response.data?.status
      ) {
        mergeCustomerSession({
          bankBreResponse: response.data,
          bank_bre_decision: response.data?.data?.decision || "",
          bank_bre_decision_reason:
            response.data?.data?.decision_reason || "",
          bank_bre_loan_amount: response.data?.data?.loan_amount || "",
        });

        const journey = persistJourneyFromApi(response.data, "approved");
        let targetScreen = journey.route || "approved";
        if (
          targetScreen === "aaCallback" ||
          targetScreen === "aaAccount"
        ) {
          targetScreen = "approved";
        }

        console.log("========== BANK BRE → NEXT ==========");
        console.log("route:", targetScreen);

        setScreen(`/apply-new/${targetScreen}`);
        return;
      }

      throw new Error(
        getApiMessage(response.data, "Bank Analyzer failed")
      );
    } catch (error) {
      console.error("========== BANK ANALYZER ERROR ==========");
      console.error(error);

      setLoading(false);
      setContinuing(false);
      setStatus("failed");
      setMessage(
        getApiMessage(
          error?.response?.data,
          error?.message || "Unable to process Bank Analyzer"
        )
      );
    }
  };

  return (
    <section className="fw-screen fw-screen--aa-success fw-fade">
      <div className="fw-brandmain">
        <img
          src={fundwalelogo}
          alt="Fundswalle"
          className="fw-brand__logo1"
        />
      </div>

      {loading && status === "checking" && (
        <div className="fw-aa-success">
          <p className="fw-aa-success__wait">Processing...</p>
        </div>
      )}

      {!loading && status === "received" && (
        <div className="fw-aa-success">
          <span className="fw-aa-success__icon" aria-hidden="true">
            <FiCheck />
          </span>
          <h1 className="fw-aa-success__title">
            Account Aggregator Completed
          </h1>
          <p className="fw-aa-success__sub">
            Your bank verification was successful.
          </p>
        </div>
      )}

      {!loading && status === "failed" && (
        <div className="fw-aa-success">
          <h1 className="fw-aa-success__title fw-aa-success__title--error">
            Something went wrong
          </h1>
          {message ? <p className="fw-error">{message}</p> : null}
        </div>
      )}

      {loading && continuing && (
        <div className="fw-aa-success">
          <p className="fw-aa-success__wait">Please wait...</p>
        </div>
      )}

      <div className="fw-screen__footer">
        {!loading && status === "received" && (
          <button
            type="button"
            className="fw-btn fw-btn--dark fw-btn--full"
            onClick={handleContinue}
            disabled={continuing}
          >
            Continue
          </button>
        )}

        {!loading && status === "failed" && (
          <button
            type="button"
            className="fw-btn fw-btn--dark fw-btn--full"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        )}
      </div>
    </section>
  );
};

export default AACallbackPage;
